import type { QueryClient } from '@tanstack/react-query'
import { dehydrate, hydrate } from '@tanstack/react-query'
import { idbGet, idbSet, idbDel, STORE_CACHE } from './idb'
import { queryClient } from './queryClient'
import { registrarFlush } from './flush'

// Persistencia del cache de react-query en IndexedDB. Escrito a mano (~80 líneas) en
// vez de @tanstack/react-query-persist-client: solo se necesita volcar y rehidratar,
// y así no entra otra dependencia al bundle inicial.
//
// POR QUÉ AQUÍ Y NO EN EL SERVICE WORKER: ver docs/brain/architecture/offline.md.
// Resumen: la Cache API indexa por URL y la API no manda Vary: Authorization, así que
// cachear /api en el SW le serviría los proyectos de una persona a la siguiente que
// abra camp en la misma máquina. Además, desde el SW react-query no puede saber si el
// dato es fresco o viejo.

const KEY = 'v1'
const MAX_AGE = 7 * 24 * 60 * 60 * 1000 // más viejo que esto no se rehidrata

// ALLOWLIST: solo estas llaves se guardan en disco. Todo lo demás vive en memoria.
// Es una lista blanca a propósito — con una lista negra, cada query nueva entraría al
// disco por omisión.
const PERSISTIBLES = new Set([
  'me', // sin esta, sin red no hay permisos y la app PARECE rota
  'prefs', // favoritos, carpetas y vista: sin ella el dashboard pierde el orden
  'projects',
  'project',
  'boards',
  'items',
  'posts',
  'post',
  'notes',
  'note',
  'timeline',
  'resources',
  'myTasks',
  'team',
  'cells',
])

// Excluidas a propósito (ver el doc):
//  · links     → los pickers meten el término de búsqueda en la llave: UNA entrada por tecla
//  · tl-cal-*  → una entrada por mes navegado, crece sin techo
//  · notifs/reminders → caducan; una notificación vieja mostrada como nueva es peor que nada
//  · trash     → restaurar sin red no puede completarse
//  · activity  → un log viejo es ruido
export function persistible(key: readonly unknown[]): boolean {
  const raiz = key[0]
  if (typeof raiz !== 'string') return false
  if (!PERSISTIBLES.has(raiz)) return false
  // Cinturón extra: nunca guardar una llave con ':' (los kinds dinámicos de links).
  return !key.some((p) => typeof p === 'string' && p.includes(':'))
}

interface Guardado {
  buster: string
  at: number
  estado: unknown
}

// El buster cuelga del hash del build: si cambia la forma de un tipo, el cache viejo
// se tira solo en vez de rehidratar objetos con campos que ya no existen.
const BUSTER = import.meta.env.VITE_BUILD_ID ?? import.meta.env.MODE

// Techo duro para la rehidratación. idb degrada a no-op si FALLA, pero puede
// COLGARSE: una petición open() se queda bloqueada si otra pestaña tiene la base
// abierta en versión distinta, o si un deleteDatabase quedó pendiente. Como
// main.tsx hace `await restaurarCache()` ANTES de montar React, una promesa que
// nunca resuelve deja la app EN BLANCO para siempre y sin ningún error en
// consola. Arrancar sin cache es molesto; no arrancar es inaceptable.
const TOPE_MS = 2500

const conTope = <T,>(p: Promise<T>, valorSiTarda: T): Promise<T> =>
  new Promise((resolve) => {
    const t = setTimeout(() => resolve(valorSiTarda), TOPE_MS)
    void p.then((v) => {
      clearTimeout(t)
      resolve(v)
    }).catch(() => {
      clearTimeout(t)
      resolve(valorSiTarda)
    })
  })

export async function restaurarCache(client: QueryClient = queryClient): Promise<void> {
  const g = await conTope(idbGet<Guardado>(STORE_CACHE, KEY), undefined)
  if (!g) return
  if (g.buster !== BUSTER || Date.now() - g.at > MAX_AGE) {
    void idbDel(STORE_CACHE, KEY)
    return
  }
  try {
    hydrate(client, g.estado)
  } catch {
    void idbDel(STORE_CACHE, KEY)
  }
}

let pendiente: ReturnType<typeof setTimeout> | null = null

function volcar(client: QueryClient): void {
  const estado = dehydrate(client, {
    shouldDehydrateQuery: (q) =>
      q.state.status === 'success' && persistible(q.queryKey as readonly unknown[]),
  })
  void idbSet(STORE_CACHE, KEY, { buster: BUSTER, at: Date.now(), estado } satisfies Guardado)
}

export function iniciarPersistencia(client: QueryClient = queryClient): void {
  // Debounce: el cache cambia en ráfagas (una pantalla dispara varias queries).
  client.getQueryCache().subscribe(() => {
    if (pendiente) clearTimeout(pendiente)
    pendiente = setTimeout(() => volcar(client), 1_000)
  })
  // Volcado inmediato al ocultar la pestaña: 'pagehide' es el único evento fiable en
  // móvil, donde el navegador puede matar la app sin previo aviso.
  const flush = () => {
    if (pendiente) clearTimeout(pendiente)
    volcar(client)
  }
  window.addEventListener('pagehide', flush)
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  // El MISMO volcado, disponible para quien tenga otro gatillo. En la app de
  // escritorio no hay 'pagehide' al cerrar la ventana ni 'visibilitychange' al
  // minimizar: el aviso llega por window.desktop.onBeforeHide y por el 'blur'
  // de la ventana (lib/flush.ts). Sin esto, cerrar la app de escritorio perdía
  // hasta un segundo de cache (el debounce) y el borrador a medio escribir.
  registrarFlush(flush)
}

// Se llama al cerrar sesión: los datos de bee no se quedan en el disco de nadie.
export async function limpiarCachePersistido(): Promise<void> {
  if (pendiente) clearTimeout(pendiente)
  await idbDel(STORE_CACHE, KEY)
}
